import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { database } from "../../firebase";
import { ref, push, set, update, serverTimestamp, onValue } from "firebase/database";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "../../components/AuthContext";
import { 
  FaPaperPlane, 
  FaArrowLeft, 
  FaRegLightbulb, 
  FaRegQuestionCircle,
  FaImage,
  FaTimes
} from "react-icons/fa";

const SupportTicketForm = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const maxChars = 2000;
  const { user } = useAuth();
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const isEditMode = !!ticketId;

  useEffect(() => {
    if (!user) {
      navigate("/login", { state: { from: isEditMode ? `/support/edit/${ticketId}` : "/support/new" } });
      return;
    }

    if (isEditMode) {
      const ticketRef = ref(database, `supportTickets/${ticketId}`);
      onValue(ticketRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setTitle(data.title || "");
          setContent(data.content || "");
          setCharCount(data.content ? data.content.length : 0);
          setExistingImages(data.imageUrls || []);
        } else {
          navigate("/support");
        }
      });
    }
  }, [user, navigate, ticketId, isEditMode]);

  const handleContentChange = (e) => {
    const value = e.target.value;
    setContent(value);
    setCharCount(value.length);
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files).filter(file => file.type.startsWith('image/'));
    if (files.length > 0) {
      setImageFiles(prev => [...prev, ...files]);
      files.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => setImagePreviews(prev => [...prev, e.target.result]);
        reader.readAsDataURL(file);
      });
    } else {
      alert("이미지 파일만 선택해주세요.");
    }
  };

  const removeImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const resizeImage = (file, maxWidth = 800, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const uploadImages = async (files) => {
    const storage = getStorage();
    const urls = [];
    for (const file of files) {
      const resizedBlob = await resizeImage(file);
      const imageRef = storageRef(storage, `supportTickets/${Date.now()}_${file.name}`);
      await uploadBytes(imageRef, resizedBlob);
      const url = await getDownloadURL(imageRef);
      urls.push(url);
    }
    return urls;
  };

  // 관리자에게 이메일 발송
  const sendEmailToAdmin = async (ticketData, ticketKey) => {
    try {
      const response = await fetch(process.env.REACT_APP_EMAIL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_EMAIL_API_KEY
        },
        body: JSON.stringify({
          to: process.env.REACT_APP_ADMIN_EMAIL,
          name: ticketData.authorName,
          subject: `[새 문의] ${ticketData.title}`,
          ticketId: ticketKey,
          ticketTitle: ticketData.title,
          ticketLink: `${window.location.origin}/support/${ticketKey}`,
          emailType: 'admin'
        })
      });

      if (!response.ok) {
        console.error('관리자 이메일 발송 실패');
      }
    } catch (error) {
      console.error('관리자 이메일 발송 오류:', error);
    }
  };

  // 고객에게 이메일 발송
  const sendEmailToCustomer = async (ticketData, ticketKey) => {
    try {
      const response = await fetch(process.env.REACT_APP_EMAIL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_EMAIL_API_KEY
        },
        body: JSON.stringify({
          to: user.email,
          name: ticketData.authorName,
          subject: `[접수완료] ${ticketData.title}`,
          ticketId: ticketKey,
          ticketTitle: ticketData.title,
          ticketLink: `${window.location.origin}/support/${ticketKey}`,
          emailType: 'customer'
        })
      });

      if (!response.ok) {
        console.error('고객 이메일 발송 실패');
      }
    } catch (error) {
      console.error('고객 이메일 발송 오류:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;

    setIsSubmitting(true);
    
    try {
      let newImageUrls = [];
      if (imageFiles.length > 0) {
        setIsUploadingImage(true);
        newImageUrls = await uploadImages(imageFiles);
        setIsUploadingImage(false);
      }

      const allImageUrls = [...existingImages, ...newImageUrls];

      const today = new Date();
      const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      if (isEditMode) {
        const ticketRef = ref(database, `supportTickets/${ticketId}`);
        await update(ticketRef, {
          title: title,
          content: content,
          imageUrls: allImageUrls,
        });
        navigate(`/support/${ticketId}`);
      } else {
        const ticketsRef = ref(database, 'supportTickets');
        const newTicketRef = push(ticketsRef);
        
        const ticketData = {
          title: title,
          content: content,
          imageUrls: allImageUrls,
          author: user.email,
          authorName: user.displayName || user.email.split('@')[0],
          createdAt: formattedDate,
          timestamp: serverTimestamp(),
          status: "접수",
          commentCount: 0
        };

        await set(newTicketRef, ticketData);

        // 이메일 발송 (관리자 & 고객)
        await Promise.all([
          sendEmailToAdmin(ticketData, newTicketRef.key),
          sendEmailToCustomer(ticketData, newTicketRef.key)
        ]);

        navigate(`/support/${newTicketRef.key}`);
      }
    } catch (error) {
      console.error("Error saving ticket:", error);
      alert("문의 저장 중 오류가 발생했습니다.");
    } finally {
      setIsSubmitting(false);
      setIsUploadingImage(false);
    }
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-blue-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate("/support")}
            className="flex items-center text-indigo-600 hover:text-indigo-800 transition-colors duration-200"
          >
            <FaArrowLeft className="mr-2" />
            <span className="font-medium">지원 게시판으로 돌아가기</span>
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 transform transition-all duration-300 hover:shadow-2xl">
          <div className="bg-gradient-to-r from-indigo-600 to-blue-500 px-6 py-8 text-white">
            <h1 className="text-3xl font-bold text-white">
              {isEditMode ? "문의 수정" : "새 문의 등록"}
            </h1>
            <p className="mt-2 opacity-90">
              {isEditMode ? "문의 내용을 수정해주세요." : "문의사항이나 지원 요청을 작성해주세요. 최대한 빠르게 답변해드리겠습니다."}
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="px-6 py-8 space-y-8">
            <div className="space-y-2">
              <label htmlFor="title" className="block text-sm font-semibold text-gray-700">
                제목
              </label>
              <div className="relative rounded-md shadow-sm">
                <input
                  type="text"
                  id="title"
                  required
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 text-gray-900 placeholder-gray-400"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="문의 제목을 입력하세요"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="content" className="block text-sm font-semibold text-gray-700 flex justify-between">
                <span>내용</span>
                <span className={`text-xs ${charCount > maxChars ? 'text-red-500 font-bold' : 'text-gray-500'}`}>
                  {charCount}/{maxChars} 자
                </span>
              </label>
              <div className="relative rounded-md shadow-sm">
                <textarea
                  id="content"
                  rows="12"
                  required
                  maxLength={maxChars}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200 text-gray-900 placeholder-gray-400 resize-none"
                  value={content}
                  onChange={handleContentChange}
                  placeholder="문의 내용을 상세히 작성해주세요"
                ></textarea>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="image" className="block text-sm font-semibold text-gray-700">
                이미지 첨부 (선택사항, 여러 장 가능)
              </label>
              <div className="relative rounded-md shadow-sm">
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                />
                <label
                  htmlFor="image"
                  className="cursor-pointer inline-flex items-center px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors duration-200"
                >
                  <FaImage className="mr-2 h-5 w-5 text-gray-400" />
                  <span className="text-gray-700">이미지 선택</span>
                </label>
                {(imageFiles.length > 0 || existingImages.length > 0) && (
                  <span className="ml-4 text-sm text-gray-500">
                    {imageFiles.length + existingImages.length}개 선택됨
                  </span>
                )}
              </div>
              
              {existingImages.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">기존 이미지</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {existingImages.map((url, index) => (
                      <div key={`existing-${index}`} className="relative">
                        <img src={url} alt={`기존 이미지 ${index + 1}`} className="w-full h-24 object-cover rounded-lg shadow-md" />
                        <button
                          type="button"
                          onClick={() => removeExistingImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                        >
                          <FaTimes className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {imagePreviews.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">새 이미지</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {imagePreviews.map((preview, index) => (
                      <div key={`new-${index}`} className="relative">
                        <img src={preview} alt={`미리보기 ${index + 1}`} className="w-full h-24 object-cover rounded-lg shadow-md" />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                        >
                          <FaTimes className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
              <div className="flex">
                <div className="flex-shrink-0">
                  <FaRegLightbulb className="h-5 w-5 text-blue-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">도움말</h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc pl-5 space-y-1">
                      <li>문제 상황을 구체적으로 설명해주세요.</li>
                      <li>관련 스크린샷이나 오류 메시지가 있다면 이미지로 첨부해주세요.</li>
                      <li>문의 등록 후 관리자 답변까지 영업일 기준 1~2일 소요될 수 있습니다.</li>
                      <li>문의 접수 시 등록하신 이메일로 확인 메일이 발송됩니다.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                className="inline-flex justify-center items-center py-3 px-6 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                onClick={() => navigate("/support")}
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isSubmitting || isUploadingImage || !title.trim() || !content.trim() || charCount > maxChars}
                className={`inline-flex justify-center items-center py-3 px-6 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white 
                  ${isSubmitting || isUploadingImage || !title.trim() || !content.trim() || charCount > maxChars
                    ? 'bg-indigo-300 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                  } transition-all duration-200 transform hover:scale-105`}
              >
                {isSubmitting || isUploadingImage ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isUploadingImage ? "이미지 업로드 중..." : `${isEditMode ? "문의 수정 중..." : "문의 등록 중..."}`}
                  </span>
                ) : (
                  <span className="flex items-center">
                    <FaPaperPlane className="mr-2 h-4 w-4" />
                    {isEditMode ? "문의 수정하기" : "문의 등록하기"}
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="mt-8 text-center">
          <div className="inline-flex items-center text-indigo-600 hover:text-indigo-800 transition-colors duration-200 cursor-pointer">
            <FaRegQuestionCircle className="mr-2" />
            <a href="/board/faq" className="font-medium">자주 묻는 질문 확인하기</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketForm;